import type { ClientUser } from '../../../shared/types/user'
import { getSettings, queueSettingsUpdate } from '../../api/settings'
import { openOverlay } from '../../components/Overlay'
import { saveHapticsPreference } from '../../utils/haptics'
import {
	applySettingsFormChange,
	getSettingsFormTarget,
} from './settingsAppFormState'
import { settingsOverlayPath } from './settingsOverlayRoutes'
import { settingsAppOverlayTemplate } from './settingsPageTemplates'
import { openSettingsSupportOverlay } from './settingsSupportOverlay'

export class SettingsAppOverlayController {
	private readonly user: ClientUser
	private formEl: HTMLFormElement | null = null

	/** Stores the current user and binds overlay handlers. */
	constructor(user: ClientUser) {
		this.user = user
		this.handleFormChange = this.handleFormChange.bind(this)
		this.handleFeedbackClick = this.handleFeedbackClick.bind(this)
	}

	/** Opens the app settings overlay with the latest settings. */
	async open(): Promise<void> {
		const pathname = window.location.pathname
		const settings = await getSettings()
		if (window.location.pathname !== pathname) return
		const user = settings ? { ...this.user, settings } : this.user

		openOverlay({
			path: settingsOverlayPath('app-settings'),
			html: settingsAppOverlayTemplate(user),
			onMount: () => this.init(),
			options: { link: false },
		})
	}

	/** Captures mounted overlay elements and initializes child controls. */
	private init(): void {
		this.formEl = document.querySelector<HTMLFormElement>(
			'[data-app-settings-form]',
		)
		if (!this.formEl) return

		this.addAppSettingsEventListeners()
	}

	/** Registers app settings form handlers. */
	private addAppSettingsEventListeners(): void {
		this.formEl?.addEventListener('change', this.handleFormChange)
		this.formEl
			?.querySelector<HTMLButtonElement>('[data-app-settings-feedback]')
			?.addEventListener('click', this.handleFeedbackClick)
	}

	/** Opens the existing contact and feedback form from app settings. */
	private handleFeedbackClick(): void {
		openSettingsSupportOverlay('contact-feedback')
	}

	private async handleFormChange(event: Event): Promise<void> {
		const target = getSettingsFormTarget(event.target)
		if (!target) return
		if (
			target instanceof HTMLInputElement &&
			target.id === 'haptics-toggle'
		) {
			// Device-only preference; saving before any await lets the switch's own feedback follow it.
			saveHapticsPreference(target.checked)
			return
		}

		const currentSettings = await getSettings()
		if (!currentSettings) return

		const settings = { ...currentSettings }
		if (!applySettingsFormChange(settings, target)) return

		queueSettingsUpdate(settings)
	}
}
